import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, ChevronRight, Mail, Calendar, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

import '@/App.css';
import { Button } from './components/ui/button';
import { Progress } from './components/ui/progress';
import ProgressStepper from './components/ProgressStepper';
import QuestionCard from './components/QuestionCard';
import EmailGateModal from './components/EmailGateModal';
import BookingModal from './components/BookingModal';

import {
  FRAMEWORK_QUESTIONS,
  ALLOCATION_QUESTIONS,
  BEHAVIOUR_QUESTIONS,
} from './data/questions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL constant
const TBNG_LOGO_URL = "https://customer-assets.emergentagent.com/job_cd1d5b1f-2926-4664-b0df-2db82ebd6f16/artifacts/edlrx9m0_Main%20Logo%20-%20H.png";
const TBNG_WEBSITE_URL = "https://tbng.co.in/";

// Thank You Page Component (shown after email submission)
const ThankYouPage = ({ investorName, email, onBookCall }) => {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="thank-you-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-center">
          <a href={TBNG_WEBSITE_URL} target="_blank" rel="noopener noreferrer">
            <img
              src={TBNG_LOGO_URL}
              alt="TBNG Capital Advisors"
              className="h-10 cursor-pointer"
            />
          </a>
        </div>
      </header>

      {/* Main Content - flex-grow to push footer down */}
      <main className="flex-grow py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', damping: 15, stiffness: 200 }}
            className="w-20 h-20 bg-[rgba(76,184,96,0.1)] rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="h-10 w-10 text-[#4CB860]" />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-2xl md:text-3xl font-serif font-bold text-[#0A0A0A] mb-3"
          >
            Thank You, {investorName}!
          </motion.h1>

          {/* Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <p className="text-base text-[#787A7A] mb-5 leading-relaxed">
              Your FAB Diagnostic Assessment has been completed successfully.
            </p>
            
            <div className="bg-[#F5F7F7] rounded-xl p-5 mb-6 inline-block">
              <div className="flex items-center justify-center gap-2 text-[#005858]">
                <Mail className="h-4 w-4" />
                <span className="font-medium text-sm">
                  Your personalised report has been sent to:
                </span>
              </div>
              <p className="text-[#0A0A0A] font-semibold mt-1">{email}</p>
            </div>

            <p className="text-[#787A7A] text-sm mb-6">
              Please check your inbox (and spam folder) for your detailed FAB Diagnostic Report 
              with personalised insights and recommendations.
            </p>
          </motion.div>

          {/* Book a Call CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="bg-gradient-to-br from-[#005858] to-[#0E302A] rounded-2xl p-6 md:p-8 text-center relative overflow-hidden"
          >
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 20px, rgba(255,255,255,0.1) 20px, rgba(255,255,255,0.1) 40px)'
              }} />
            </div>
            
            <div className="relative">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              
              <h2 className="text-xl font-serif font-bold text-white mb-2">
                Your Next Step
              </h2>
              <p className="text-white/80 text-sm max-w-md mx-auto mb-5">
                Schedule a FAB Diagnostic Call with our advisors to discuss your results 
                and develop a personalised investment strategy.
              </p>
              
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSeOQ_RdseeakXkVN3BIu6yk0HYNv-LWNcGvPQfw_GOH9HMZbQ/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-12 px-8 bg-white text-[#005858] hover:bg-[#F5F7F7] font-semibold rounded-lg items-center justify-center gap-2 mx-auto transition-all"
                data-testid="book-call-cta-btn"
              >
                <Calendar className="h-4 w-4" />
                Book A Call
              </a>
              
              <p className="text-white/60 text-xs mt-4">
                30-minute complimentary consultation with a TBNG Investment Advisor
              </p>
            </div>
          </motion.div>

          {/* Contact Info - Fixed email */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="text-sm text-[#787A7A] mt-6"
          >
            Questions? Contact us at{' '}
            <a href="mailto:connect@tbng.co.in" className="text-[#005858] hover:underline">
              connect@tbng.co.in
            </a>
          </motion.p>
        </div>
      </main>

      {/* Footer - White background with logo hyperlinked */}
      <footer className="bg-white border-t border-[#E2E8E8] py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <a href={TBNG_WEBSITE_URL} target="_blank" rel="noopener noreferrer">
            <img
              src={TBNG_LOGO_URL}
              alt="TBNG Capital Advisors"
              className="h-8 mx-auto mb-3 cursor-pointer"
            />
          </a>
          <p className="text-[#787A7A] text-xs">
            © {new Date().getFullYear()} TBNG Capital Advisors. Nurturing Your Wealth.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Landing Page Component
const LandingPage = ({ onStart }) => {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-[#E2E8E8] z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <a href={TBNG_WEBSITE_URL} target="_blank" rel="noopener noreferrer">
            <img
              src={TBNG_LOGO_URL}
              alt="TBNG Capital Advisors"
              className="h-10 cursor-pointer"
              data-testid="tbng-logo"
            />
          </a>
          <Button
            onClick={onStart}
            className="h-11 px-6 bg-[#005858] hover:bg-[#0E302A] text-white font-medium rounded-lg transition-all"
            data-testid="header-start-btn"
          >
            Start Diagnostic
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="pt-24">
        <div className="relative overflow-hidden">
          {/* Diagonal pattern */}
          <div className="absolute inset-0 diagonal-pattern opacity-50" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative">
            <div className="max-w-3xl mx-auto text-center">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-block px-4 py-1 bg-[rgba(0,88,88,0.1)] text-[#005858] rounded-full text-sm font-medium mb-6"
              >
                Proprietary Advisory Diagnostic
              </motion.span>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-[#0A0A0A] mb-6 tracking-tight"
              >
                FAB Diagnostic
                <span className="block text-[#005858]">Assessment</span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg md:text-xl text-[#787A7A] mb-10 max-w-2xl mx-auto leading-relaxed"
              >
                Evaluate your investment approach across three critical dimensions: 
                <span className="text-[#005858] font-medium"> Framework</span>, 
                <span className="text-[#005858] font-medium"> Asset Allocation</span>, and 
                <span className="text-[#005858] font-medium"> Behaviour</span>.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Button
                  onClick={onStart}
                  className="h-14 px-10 bg-[#005858] hover:bg-[#0E302A] text-white font-medium rounded-lg text-lg transition-all shadow-lg hover:shadow-xl flex items-center gap-2 mx-auto"
                  data-testid="hero-start-btn"
                >
                  Begin Assessment
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </motion.div>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="text-sm text-[#787A7A] mt-6"
              >
                15 questions · 5 minutes · Personalized insights
              </motion.p>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-[#F5F7F7] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif font-bold text-[#0A0A0A] mb-4">
                What You'll Discover
              </h2>
              <p className="text-[#787A7A] max-w-2xl mx-auto">
                A comprehensive analysis of your investment discipline across three core dimensions
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Framework',
                  description: 'Assess the structure and documentation of your investment approach, goals, and decision-making process.',
                  questions: '5 Questions'
                },
                {
                  title: 'Asset Allocation',
                  description: 'Evaluate how well your portfolio is diversified and aligned with your risk capacity and time horizon.',
                  questions: '5 Questions'
                },
                {
                  title: 'Behaviour',
                  description: 'Understand your emotional responses to market movements and how they impact your investment decisions.',
                  questions: '5 Questions'
                }
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 border border-[#E2E8E8] hover:shadow-lg transition-all duration-300"
                  data-testid={`feature-card-${item.title.toLowerCase()}`}
                >
                  <span className="inline-block w-10 h-10 rounded-lg bg-[rgba(0,88,88,0.1)] text-[#005858] font-bold flex items-center justify-center mb-4 text-lg">
                    {index + 1}
                  </span>
                  <h3 className="text-xl font-serif font-bold text-[#0A0A0A] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-[#787A7A] text-sm mb-4 leading-relaxed">
                    {item.description}
                  </p>
                  <span className="text-xs font-medium text-[#005858] uppercase tracking-wider">
                    {item.questions}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0E302A] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <a href={TBNG_WEBSITE_URL} target="_blank" rel="noopener noreferrer">
            <img
              src={TBNG_LOGO_URL}
              alt="TBNG Capital Advisors"
              className="h-10 mx-auto mb-4 brightness-0 invert cursor-pointer"
            />
          </a>
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} TBNG Capital Advisors. Nurturing Your Wealth.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Main App Component
function App() {
  // State - Start directly on quiz (skip landing page)
  const [currentView, setCurrentView] = useState('quiz'); // landing, quiz, thankyou
  const [currentStep, setCurrentStep] = useState(1); // 1, 2, 3
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  // Answers state
  const [frameworkAnswers, setFrameworkAnswers] = useState({});
  const [allocationAnswers, setAllocationAnswers] = useState({});
  const [behaviourAnswers, setBehaviourAnswers] = useState({});
  
  // Modal states
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  
  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Results
  const [diagnosticResult, setDiagnosticResult] = useState(null);

  // Get current questions based on step
  const getCurrentQuestions = () => {
    switch (currentStep) {
      case 1: return FRAMEWORK_QUESTIONS;
      case 2: return ALLOCATION_QUESTIONS;
      case 3: return BEHAVIOUR_QUESTIONS;
      default: return [];
    }
  };

  // Get current answers based on step
  const getCurrentAnswers = () => {
    switch (currentStep) {
      case 1: return frameworkAnswers;
      case 2: return allocationAnswers;
      case 3: return behaviourAnswers;
      default: return {};
    }
  };

  // Set answer for current step
  const setCurrentAnswer = (questionId, value) => {
    switch (currentStep) {
      case 1:
        setFrameworkAnswers(prev => ({ ...prev, [questionId]: value }));
        break;
      case 2:
        setAllocationAnswers(prev => ({ ...prev, [questionId]: value }));
        break;
      case 3:
        setBehaviourAnswers(prev => ({ ...prev, [questionId]: value }));
        break;
    }
  };

  const questions = getCurrentQuestions();
  const answers = getCurrentAnswers();
  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestionsInStep = questions.length;

  // Calculate overall progress
  const calculateProgress = () => {
    const totalQuestions = FRAMEWORK_QUESTIONS.length + ALLOCATION_QUESTIONS.length + BEHAVIOUR_QUESTIONS.length;
    let answered = 0;
    
    if (currentStep >= 1) answered += Object.keys(frameworkAnswers).length;
    if (currentStep >= 2) answered += Object.keys(allocationAnswers).length;
    if (currentStep >= 3) answered += Object.keys(behaviourAnswers).length;
    
    return (answered / totalQuestions) * 100;
  };

  // Check if current step is complete
  const isStepComplete = () => {
    return Object.keys(answers).length === totalQuestionsInStep;
  };

  // Handle answer selection - NO auto-advance to prevent question skipping
  const handleAnswer = (questionId, value) => {
    setCurrentAnswer(questionId, value);
    // User must manually click Next Question button to advance
  };

  // Handle next step/finish
  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
      setCurrentQuestionIndex(0);
    } else {
      // Show email gate
      setShowEmailGate(true);
    }
  };

  // Handle previous
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      const prevQuestions = currentStep === 2 ? FRAMEWORK_QUESTIONS : ALLOCATION_QUESTIONS;
      setCurrentQuestionIndex(prevQuestions.length - 1);
    }
  };

  // Format answers for API - Now uses actual point values from options
  const formatAnswersForAPI = (answersObj, questions) => {
    return questions.map(q => ({
      question_id: q.id,
      answer_value: answersObj[q.id] ?? 1 // Default to 1 (lowest score) if not answered
    }));
  };

  // Submit diagnostic
  const handleSubmitDiagnostic = async (userData) => {
    setIsSubmitting(true);
    
    try {
      const payload = {
        investor_name: userData.name,
        email: userData.email,
        designation: userData.designation,
        contact_number: userData.contactNumber,
        framework_answers: formatAnswersForAPI(frameworkAnswers, FRAMEWORK_QUESTIONS),
        allocation_answers: formatAnswersForAPI(allocationAnswers, ALLOCATION_QUESTIONS),
        behaviour_answers: formatAnswersForAPI(behaviourAnswers, BEHAVIOUR_QUESTIONS),
      };

      const response = await axios.post(`${API}/diagnostic/submit`, payload);
      
      setDiagnosticResult(response.data);
      setShowEmailGate(false);
      setCurrentView('thankyou'); // Show thank you page instead of results
      
      toast.success('Your FAB Diagnostic report has been sent to your email!');
      
    } catch (error) {
      console.error('Error submitting diagnostic:', error);
      toast.error('Failed to submit diagnostic. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Start quiz
  const handleStart = () => {
    setCurrentView('quiz');
  };

  // Render based on view
  if (currentView === 'landing') {
    return (
      <>
        <Toaster position="top-right" richColors />
        <LandingPage onStart={handleStart} />
      </>
    );
  }

  if (currentView === 'thankyou') {
    return (
      <>
        <Toaster position="top-right" richColors />
        <ThankYouPage 
          investorName={diagnosticResult?.investor_name} 
          email={diagnosticResult?.email}
          onBookCall={() => setShowBookingModal(true)}
        />
        <BookingModal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
        />
      </>
    );
  }

  // Quiz View
  return (
    <div className="min-h-screen bg-white" data-testid="quiz-view">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-[#E2E8E8] z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            <a href={TBNG_WEBSITE_URL} target="_blank" rel="noopener noreferrer">
              <img
                src={TBNG_LOGO_URL}
                alt="TBNG Capital Advisors"
                className="h-8 cursor-pointer"
              />
            </a>
            <span className="text-sm text-[#787A7A]">
              {Math.round(calculateProgress())}% Complete
            </span>
          </div>
          
          {/* Progress bar */}
          <Progress value={calculateProgress()} className="h-2 bg-[#F5F7F7]" data-testid="overall-progress" />
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-32 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Stepper */}
          <div className="mb-10">
            <ProgressStepper currentStep={currentStep} />
          </div>

          {/* Step Title */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#0A0A0A]">
              {currentStep === 1 && 'Framework Assessment'}
              {currentStep === 2 && 'Asset Allocation Assessment'}
              {currentStep === 3 && 'Behaviour Assessment'}
            </h2>
            <p className="text-[#787A7A] mt-2">
              Question {currentQuestionIndex + 1} of {totalQuestionsInStep}
            </p>
          </motion.div>

          {/* Question Card */}
          <AnimatePresence mode="wait">
            {currentQuestion && (
              <QuestionCard
                key={currentQuestion.id}
                question={currentQuestion}
                questionIndex={currentQuestionIndex}
                totalQuestions={totalQuestionsInStep}
                selectedValue={answers[currentQuestion.id]}
                onAnswer={handleAnswer}
              />
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Navigation Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E8E8] py-4 px-4 z-40">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Button
            onClick={handlePrevious}
            variant="ghost"
            disabled={currentStep === 1 && currentQuestionIndex === 0}
            className="flex items-center gap-2 text-[#787A7A] hover:text-[#005858]"
            data-testid="prev-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>

          {currentQuestionIndex < totalQuestionsInStep - 1 ? (
            <Button
              onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
              disabled={answers[currentQuestion?.id] === undefined}
              className="flex items-center gap-2 bg-[#005858] hover:bg-[#0E302A] text-white"
              data-testid="next-question-btn"
            >
              Next Question
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!isStepComplete()}
              className="flex items-center gap-2 bg-[#005858] hover:bg-[#0E302A] text-white"
              data-testid="next-step-btn"
            >
              {currentStep < 3 ? 'Continue to Next Section' : 'View Results'}
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </footer>

      {/* Email Gate Modal */}
      <EmailGateModal
        isOpen={showEmailGate}
        onClose={() => setShowEmailGate(false)}
        onSubmit={handleSubmitDiagnostic}
        isLoading={isSubmitting}
      />

      {/* Booking Modal */}
      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
      />
    </div>
  );
}

export default App;
